// ../../react-shim.js
import React from "react";

// src/number-input.tsx
import { useFormControlProps } from "@nature-ui/form-control";
import { Input } from "@nature-ui/input";
import { createContext } from "@nature-ui/react-utils";
import {
  clsx,
  forwardRef,
  nature
} from "@nature-ui/system";
import { __DEV__ } from "@nature-ui/utils";
import React3 from "react";

// src/icons.tsx
import { Icon } from "@nature-ui/icon";
import { jsx } from "react/jsx-runtime";
var TriangleDownIcon = (props) => /* @__PURE__ */ jsx(Icon, {
  viewBox: "0 0 24 24",
  ...props,
  children: /* @__PURE__ */ jsx("path", {
    fill: "currentColor",
    d: "M21,5H3C2.621,5,2.275,5.214,2.105,5.553C1.937,5.892,1.973,6.297,2.2,6.6l9,12 c0.188,0.252,0.485,0.4,0.8,0.4s0.611-0.148,0.8-0.4l9-12c0.228-0.303,0.264-0.708,0.095-1.047C21.725,5.214,21.379,5,21,5z"
  })
});
var TriangleUpIcon = (props) => /* @__PURE__ */ jsx(Icon, {
  viewBox: "0 0 24 24",
  ...props,
  children: /* @__PURE__ */ jsx("path", {
    fill: "currentColor",
    d: "M12.8,5.4c-0.377-0.504-1.223-0.504-1.6,0l-9,12c-0.228,0.303-0.264,0.708-0.095,1.047 C2.275,18.786,2.621,19,3,19h18c0.379,0,0.725-0.214,0.895-0.553c0.169-0.339,0.133-0.744-0.095-1.047L12.8,5.4z"
  })
});

// src/use-number-input.ts
import { useCounter } from "@nature-ui/counter";
import {
  useBoolean,
  useCallbackRef,
  useEventListener,
  useSafeLayoutEffect
} from "@nature-ui/hooks";
import {
  ariaAttr,
  callAllHandler,
  focus,
  isBrowser,
  isNull,
  maxSafeInteger,
  mergeRefs,
  minSafeInteger,
  normalizeEventKey
} from "@nature-ui/utils";
import React2, { useCallback as useCallback2, useMemo } from "react";

// src/use-spinner.ts
import { useInterval, useUnmountEffect } from "@nature-ui/hooks";
import { useCallback, useRef, useState } from "react";
var CONTINUOS_CHANGE_INTERVAL = 50;
var CONTINUOS_CHANGE_DELAY = 300;
var useSpinner = (increment, decrement) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [action, setAction] = useState(null);
  const [runOnce, setRunOnce] = useState(true);
  const timeoutRef = useRef(null);
  const removeTimeout = () => clearTimeout(timeoutRef.current);
  useInterval(
    () => {
      if (action === "increment")
        increment();
      else if (action === "decrement")
        decrement();
    },
    isSpinning ? CONTINUOS_CHANGE_INTERVAL : null
  );
  const up = useCallback(() => {
    if (runOnce)
      increment();
    timeoutRef.current = setTimeout(() => {
      setRunOnce(false);
      setIsSpinning(true);
      setAction("increment");
    }, CONTINUOS_CHANGE_DELAY);
  }, [increment, runOnce]);
  const down = useCallback(() => {
    if (runOnce)
      decrement();
    timeoutRef.current = setTimeout(() => {
      setRunOnce(false);
      setIsSpinning(true);
      setAction("decrement");
    }, CONTINUOS_CHANGE_DELAY);
  }, [decrement, runOnce]);
  const stop = useCallback(() => {
    setRunOnce(true);
    setIsSpinning(false);
    removeTimeout();
  }, []);
  useUnmountEffect(removeTimeout);
  return { up, down, stop };
};

// src/utils.ts
var FLOATING_POINT_REGEX = /^[Ee0-9+\-.]$/;
var isFloatingPointNumericCharacter = (character) => FLOATING_POINT_REGEX.test(character);
var isValidNumericKeyboardEvent = (event) => {
  if (event.key == null)
    return true;
  const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
  if (isModifierKey)
    return true;
  const isSingleCharacterKey = event.key.length === 1;
  if (!isSingleCharacterKey)
    return true;
  return isFloatingPointNumericCharacter(event.key);
};

// src/use-number-input.ts
var sanitize = (value) => value.split("").filter(isFloatingPointNumericCharacter).join("");
var useNumberInput = (props = {}) => {
  const {
    focusInputOnChange = true,
    clampValueOnBlur = true,
    keepWithinRange = true,
    min = minSafeInteger,
    max = maxSafeInteger,
    step: stepProp = 1,
    isReadOnly,
    isDisabled,
    isRequired,
    getAriaValueText,
    isInvalid,
    pattern = "[0-9]*(.[0-9]+)?",
    inputMode = "decimal",
    allowMouseWheel,
    id,
    onChange: _,
    precision,
    name,
    "aria-describedby": ariaDescBy,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    onFocus,
    onBlur,
    ...htmlProps
  } = props;
  const onFocusProp = useCallbackRef(onFocus);
  const onBlurProp = useCallbackRef(onBlur);
  const getAriaValueTextProp = useCallbackRef(getAriaValueText);
  const counter = useCounter(props);
  const {
    update: updateFn,
    increment: incrementFn,
    decrement: decrementFn
  } = counter;
  const [isFocused, setFocused] = useBoolean();
  const inputRef = React2.useRef(null);
  const getStepFactor = (event) => {
    let ratio = 1;
    if (event.metaKey || event.ctrlKey) {
      ratio = 0.1;
    }
    if (event.shiftKey) {
      ratio = 10;
    }
    return ratio;
  };
  useSafeLayoutEffect(() => {
    if (!inputRef.current)
      return;
    const notInSync = inputRef.current.value !== String(counter.value);
    if (notInSync) {
      counter.setValue(sanitize(inputRef.current.value));
    }
  }, []);
  const isInteractive = !(isReadOnly || isDisabled);
  const increment = useCallback2(
    (step = stepProp) => {
      if (isInteractive) {
        incrementFn(step);
      }
    },
    [incrementFn, isInteractive, stepProp]
  );
  const decrement = useCallback2(
    (step = stepProp) => {
      if (isInteractive) {
        decrementFn(step);
      }
    },
    [decrementFn, isInteractive, stepProp]
  );
  const spinner = useSpinner(increment, decrement);
  const onChange = useCallback2(
    (event) => {
      updateFn(sanitize(event.target.value));
    },
    [updateFn]
  );
  const onKeyDown = React2.useCallback(
    (event) => {
      if (!isValidNumericKeyboardEvent(event)) {
        event.preventDefault();
      }
      const stepFactor = getStepFactor(event) * stepProp;
      const eventKey = normalizeEventKey(event);
      const keyMap = {
        ArrowUp: () => increment(stepFactor),
        ArrowDown: () => decrement(stepFactor),
        Home: () => updateFn(min),
        End: () => updateFn(max)
      };
      const action = keyMap[eventKey];
      if (action) {
        event.preventDefault();
        action(event);
      }
    },
    [stepProp, increment, decrement, updateFn, min, max]
  );
  const ariaValueText = useMemo(() => {
    const text = getAriaValueTextProp == null ? void 0 : getAriaValueTextProp(counter.value);
    if (!isNull(text)) {
      return text;
    }
    const defaultText = counter.value.toString();
    return !defaultText ? void 0 : defaultText;
  }, [counter.value, getAriaValueTextProp]);
  const validateAndClamp = useCallback2(() => {
    let next = counter.value;
    if (next === "")
      return;
    if (counter.valueAsNumber < min) {
      next = min;
    }
    if (counter.valueAsNumber > max) {
      next = max;
    }
    counter.cast(next);
  }, [counter, max, min]);
  const onInputBlur = React2.useCallback(() => {
    setFocused.off();
    if (clampValueOnBlur) {
      validateAndClamp();
    }
  }, [clampValueOnBlur, setFocused, validateAndClamp]);
  const focusInput = useCallback2(() => {
    if (focusInputOnChange) {
      focus(inputRef.current, { nextTick: true });
    }
  }, [focusInputOnChange]);
  const spinUp = React2.useCallback(
    (event) => {
      event.preventDefault();
      spinner.up();
      focusInput();
    },
    [focusInput, spinner]
  );
  const spinDown = React2.useCallback(
    (event) => {
      event.preventDefault();
      spinner.down();
      focusInput();
    },
    [focusInput, spinner]
  );
  const pointerDown = isBrowser && !!document.documentElement.ontouchstart ? "onTouchStart" : "onMouseDown";
  useEventListener(
    "wheel",
    (event) => {
      const isInputFocused = document.activeElement === inputRef.current;
      if (!allowMouseWheel || !isInputFocused)
        return;
      event.preventDefault();
      const stepFactor = getStepFactor(event) * stepProp;
      const direction = Math.sign(event.deltaY);
      if (direction === -1) {
        increment(stepFactor);
      } else if (direction === 1) {
        decrement(stepFactor);
      }
    },
    inputRef.current,
    { passive: false }
  );
  const getIncrementButtonProps = useCallback2(
    (props2 = {}, ref = null) => {
      const disabled = isDisabled || keepWithinRange && counter.isAtMax;
      return {
        ...props2,
        ref,
        role: "button",
        tabIndex: -1,
        [pointerDown]: callAllHandler(props2[pointerDown], spinUp),
        onMouseUp: callAllHandler(props2.onMouseUp, spinner.stop),
        onMouseLeave: callAllHandler(props2.onMouseUp, spinner.stop),
        onTouchEnd: callAllHandler(props2.onTouchEnd, spinner.stop),
        disabled,
        "aria-disabled": ariaAttr(disabled)
      };
    },
    [
      pointerDown,
      counter.isAtMax,
      keepWithinRange,
      spinUp,
      spinner.stop,
      isDisabled
    ]
  );
  const getDecrementButtonProps = useCallback2(
    (props2 = {}, ref = null) => {
      const disabled = isDisabled || keepWithinRange && counter.isAtMin;
      return {
        ...props2,
        ref,
        role: "button",
        tabIndex: -1,
        [pointerDown]: callAllHandler(props2[pointerDown], spinDown),
        onMouseLeave: callAllHandler(props2.onMouseLeave, spinner.stop),
        onMouseUp: callAllHandler(props2.onMouseUp, spinner.stop),
        onTouchEnd: callAllHandler(props2.onTouchEnd, spinner.stop),
        disabled,
        "aria-disabled": ariaAttr(disabled)
      };
    },
    [
      pointerDown,
      counter.isAtMin,
      keepWithinRange,
      spinDown,
      spinner.stop,
      isDisabled
    ]
  );
  const getInputProps = useCallback2(
    (props2 = {}, ref = null) => ({
      name,
      inputMode,
      type: "text",
      pattern,
      "aria-labelledby": ariaLabelledBy,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescBy,
      id,
      disabled: isDisabled,
      ...props2,
      readOnly: props2.readOnly ?? isReadOnly,
      "aria-readonly": props2.readOnly ?? isReadOnly,
      "aria-required": props2.required ?? isRequired,
      required: props2.required ?? isRequired,
      ref: mergeRefs(inputRef, ref),
      value: counter.value,
      role: "spinbutton",
      "aria-valuemin": min,
      "aria-valuemax": max,
      "aria-valuenow": Number.isNaN(counter.valueAsNumber) ? void 0 : counter.valueAsNumber,
      "aria-invalid": ariaAttr(isInvalid ?? counter.isOutOfRange),
      "aria-valuetext": ariaValueText,
      autoComplete: "off",
      autoCorrect: "off",
      onChange: callAllHandler(props2.onChange, onChange),
      onKeyDown: callAllHandler(props2.onKeyDown, onKeyDown),
      onFocus: callAllHandler(props2.onFocus, onFocusProp, setFocused.on),
      onBlur: callAllHandler(props2.onBlur, onBlurProp, onInputBlur)
    }),
    [
      name,
      inputMode,
      pattern,
      ariaLabelledBy,
      ariaLabel,
      ariaDescBy,
      id,
      isDisabled,
      isRequired,
      isReadOnly,
      isInvalid,
      counter.value,
      counter.valueAsNumber,
      counter.isOutOfRange,
      min,
      max,
      ariaValueText,
      onChange,
      onKeyDown,
      onFocusProp,
      setFocused.on,
      onBlurProp,
      onInputBlur
    ]
  );
  return {
    value: counter.value,
    valueAsNumber: counter.valueAsNumber,
    isFocused,
    isDisabled,
    isReadOnly,
    getIncrementButtonProps,
    getDecrementButtonProps,
    getInputProps,
    htmlProps
  };
};

// src/number-input.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var [NumberInputProvider, useNumberInputContext] = createContext({
  name: "NumberInputContext",
  errorMessage: "useNumberInputContext: `context` is undefined. Seems you forgot to wrap number-input's components withing <NumberInput />"
});
var NumberInputWrapper = forwardRef(
  (props, ref) => {
    const controlProps = useFormControlProps(props);
    const { htmlProps, ...context } = useNumberInput(controlProps);
    const ctx = React3.useMemo(() => context, [context]);
    return /* @__PURE__ */ jsx2(NumberInputProvider, {
      value: ctx,
      children: /* @__PURE__ */ jsx2(nature.div, {
        ref,
        ...htmlProps,
        className: clsx("relative z-0", props.className),
        css: {
          "&::before": {}
        }
      })
    });
  }
);
if (__DEV__) {
  NumberInputWrapper.displayName = "NumberInput";
}
var NumberInputStepper = forwardRef(
  (props, ref) => {
    return /* @__PURE__ */ jsx2(nature.div, {
      ...props,
      className: clsx(
        "flex flex-col absolute top-0 right-0 mr-1 w-6 z-10",
        props.className
      ),
      css: {
        marginRight: "1px",
        height: "calc(100% - 2px);"
      },
      "aria-hidden": true,
      ref
    });
  }
);
if (__DEV__) {
  NumberInputStepper.displayName = "NumberInputStepper";
}
var NumberInputField = forwardRef(
  (props, ref) => {
    const { getInputProps } = useNumberInputContext();
    const input = getInputProps(props, ref);
    return /* @__PURE__ */ jsx2(Input, {
      ...input
    });
  }
);
if (__DEV__) {
  NumberInputField.displayName = "NumberInputField";
}
var stepperStyles = {
  borderInlineStart: "1px solid",
  borderInlineStartColor: "inherit",
  color: "inherit",
  fontSize: "calc(1rem * 0.75)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flex: 1,
  userSelect: "none",
  lineHeight: "normal",
  cursor: "pointer"
};
var NumberDecrementStepper = forwardRef((props, ref) => {
  const { getDecrementButtonProps } = useNumberInputContext();
  const decrement = getDecrementButtonProps(props, ref);
  return /* @__PURE__ */ jsx2(nature.div, {
    ...decrement,
    css: { ...stepperStyles, ...decrement.css },
    className: clsx(
      {
        ["cursor-not-allowed opacity-40"]: decrement.disabled
      },
      decrement.className
    ),
    children: props.children ?? /* @__PURE__ */ jsx2(TriangleDownIcon, {})
  });
});
if (__DEV__) {
  NumberDecrementStepper.displayName = "NumberIncrementStepper";
}
var NumberIncrementStepper = forwardRef((props, ref) => {
  const { getIncrementButtonProps } = useNumberInputContext();
  const increment = getIncrementButtonProps(props, ref);
  return /* @__PURE__ */ jsx2(nature.div, {
    ...increment,
    css: { ...stepperStyles, ...increment.css },
    className: clsx(
      {
        ["cursor-not-allowed opacity-40"]: increment.disabled
      },
      increment.className
    ),
    children: props.children ?? /* @__PURE__ */ jsx2(TriangleUpIcon, {})
  });
});
if (__DEV__) {
  NumberIncrementStepper.displayName = "NumberIncrementStepper";
}
var NumberInput = forwardRef(
  (props, ref) => {
    const {
      inputProps,
      stepperProps,
      incrementStepperProps,
      decrementStepperProps,
      ...rest
    } = props;
    return /* @__PURE__ */ jsxs(NumberInputWrapper, {
      ...rest,
      children: [
        /* @__PURE__ */ jsx2(NumberInputField, {
          ...inputProps,
          ref
        }),
        /* @__PURE__ */ jsxs(NumberInputStepper, {
          ...stepperProps,
          children: [
            /* @__PURE__ */ jsx2(NumberIncrementStepper, {
              ...incrementStepperProps
            }),
            /* @__PURE__ */ jsx2(NumberDecrementStepper, {
              ...decrementStepperProps
            })
          ]
        })
      ]
    });
  }
);
export {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberInputWrapper,
  useNumberInput
};
